/**
 * postgrest-filters
 *
 * Helpers for building raw PostgREST logic filters safely.
 *
 * Primary responsibilities:
 * - Quote user-provided values for PostgREST logic trees
 * - Build small OR filters for Supabase `.or(...)` calls
 *
 * Dependencies:
 * - lib/search/tokenize-query
 */

import { tokenizeSearchQuery } from '@/lib/search/tokenize-query'

type PostgrestLogicOperator = 'eq' | 'ilike'

type PostgrestLogicFilter = {
  column: string
  operator: PostgrestLogicOperator
  value: string
}

const POSTGREST_LOGIC_COLUMN_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*$/

export function quotePostgrestLogicValue(value: string) {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
}

export function buildPostgrestLogicFilter({ column, operator, value }: PostgrestLogicFilter) {
  if (!POSTGREST_LOGIC_COLUMN_PATTERN.test(column)) {
    throw new Error(`Invalid PostgREST logic column: ${column}`)
  }

  return `${column}.${operator}.${quotePostgrestLogicValue(value)}`
}

export function buildPostgrestOrFilter(filters: PostgrestLogicFilter[]) {
  return filters.map(buildPostgrestLogicFilter).join(',')
}

export function buildPostgrestIlikeOrFilter(columns: string[], value: string) {
  return buildPostgrestOrFilter(
    columns.map((column) => ({
      column,
      operator: 'ilike' as const,
      value: `%${value}%`
    }))
  )
}

// A plain ilike OR filter requires the *entire* query to appear verbatim as
// one substring — natural-language queries like "job search applications
// interviews rejections" almost never match anything that way. This variant
// splits the query into individual keywords and matches a column against
// any of them, which is what callers actually want for free-text search.
export function buildPostgrestTokenizedIlikeOrFilter(columns: string[], value: string) {
  const tokens = tokenizeSearchQuery(value)

  return buildPostgrestOrFilter(
    columns.flatMap((column) =>
      tokens.map((token) => ({
        column,
        operator: 'ilike' as const,
        value: `%${token}%`
      }))
    )
  )
}
